import { Link } from '../Link/Link'
import { styled } from '@aether/styles'
import { ArrowRight } from '@aether/icons'
import { PageLink, RsvpModalLinkAction } from '@aether/models'
import { useEffect, useState } from 'react'
import { getListProfiles } from '@aether/services/api-service'
import { useTranslation } from 'react-i18next'
import { Loader } from '../Loader/Loader'

const BUTTON_MAX_WIDTH = '350px'

const PrimaryCtaButton = styled(Link, {
  variants: {
    variant: {
      link: {},
      vip: {
        backgroundColor: '$blue',
        color: '$white',
        '& svg': {
          color: '$white',
        },
      },
      waitlist: {
        backgroundColor: '$gray_light_third',
        color: '$black',
      },
    },
    isCardVariant: {
      true: {
        width: 'auto',
        maxWidth: 'none',
      },
      false: {
        width: 'calc(100vw - 2 * $16)',
        maxWidth: BUTTON_MAX_WIDTH,
        '@lg': {
          width: 'auto',
          maxWidth: 'none',
        },
      },
    },
  },
  defaultVariants: {
    isCardVariant: false,
  },
})

const ArrowIcon = styled(ArrowRight, {
  '& path': {
    fill: 'currentColor',
  },
  variants: {
    isExternal: {
      true: {
        transform: 'rotate(-45deg)',
      },
    },
  },
})

const ButtonContent = styled('div', {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$8',
})

interface EventButtonProps {
  eventData: {
    isVipEvent: boolean
    isRsvpEnded: boolean
    primaryCta?: {
      label?: string
      moreInformation?: string
      link?: PageLink
    }
  }
  isVip: boolean
  isCardVariant?: boolean
  overrideLabel?: string
}

export const EventButton: React.FC<EventButtonProps> = ({
  eventData,
  isVip,
  isCardVariant,
  overrideLabel,
}) => {
  const [seatsAvailable, setSeatsAvailable] = useState<number | null>(null)
  const [isFetchingSeats, setIsFetchingSeats] = useState(false)
  const [isRSVPListFull, setIsRSVPListFull] = useState(false)
  const { t } = useTranslation('eventButton')

  useEffect(() => {
    const rsvpAction = eventData.primaryCta?.link?.action
    if (
      rsvpAction?.type === 'rsvpModalLinkAction' &&
      rsvpAction.payload?.listId &&
      rsvpAction.payload.maxRSVP
    ) {
      setIsFetchingSeats(true)
      getListProfiles(rsvpAction.payload.listId)
        .then((res) => {
          const currentCount = res?.data?.length || 0
          const available = rsvpAction.payload.maxRSVP! - currentCount
          setSeatsAvailable(available)
          if (available <= 0) setIsRSVPListFull(true)
        })
        .finally(() => setIsFetchingSeats(false))
    }
  }, [eventData.primaryCta])

  const getPrimaryCtaSettings = () => {
    const action = eventData.primaryCta?.link?.action
    const isRsvpAction = action?.type === 'rsvpModalLinkAction'
    const isRsvpClosed = isRSVPListFull || eventData.isRsvpEnded
    const isWaitlist =
      isRsvpAction &&
      isRsvpClosed &&
      !!(action as RsvpModalLinkAction).payload?.waitlistModalId

    const isDisabled =
      isFetchingSeats ||
      (eventData.isVipEvent && !isVip) ||
      (isRsvpAction && isRsvpClosed && !isWaitlist)
    const hasExternalLink = eventData.primaryCta?.link?.href?.startsWith('http')
    const label = eventData.primaryCta?.label
    let moreInfo = eventData.primaryCta?.moreInformation

    if (isWaitlist) {
      moreInfo = t('joinWaitlist') as string
    }

    if (isRsvpAction && seatsAvailable && !isRsvpClosed) {
      moreInfo = t('seatsAvailable', { value: seatsAvailable }) as string
    }

    let link = eventData.primaryCta?.link
    if (isWaitlist && link?.action?.type === 'rsvpModalLinkAction') {
      const rsvpAction = link.action
      link = {
        ...link,
        action: {
          ...rsvpAction,
          payload: {
            ...rsvpAction.payload,
            modalId: rsvpAction.payload.waitlistModalId,
          },
        },
      }
    }

    return {
      isWaitlist,
      isDisabled,
      hasExternalLink,
      label,
      moreInfo,
      link,
      isRsvpAction,
    }
  }

  const {
    isWaitlist,
    isRsvpAction,
    isDisabled,
    hasExternalLink,
    label,
    moreInfo,
    link,
  } = getPrimaryCtaSettings()

  const getPrimaryCtaVariant = (): 'link' | 'vip' | 'waitlist' => {
    if (isCardVariant) return 'link'
    if (eventData.isVipEvent) return 'vip'
    if (isWaitlist) return 'waitlist'
    return 'link'
  }

  const shouldHideOnCard = isWaitlist || isDisabled || !isRsvpAction
  if (isCardVariant && shouldHideOnCard) return null

  return (
    <PrimaryCtaButton
      active={false}
      disabled={isDisabled}
      appearance={isCardVariant ? 'primary' : 'badgeBlack'}
      variant={getPrimaryCtaVariant()}
      isCardVariant={isCardVariant}
      {...link}
    >
      {isFetchingSeats ? (
        <Loader />
      ) : (
        <>
          <ButtonContent>
            {overrideLabel ? (
              overrideLabel
            ) : (
              <>
                {label}
                {!!moreInfo && <span>{moreInfo}</span>}
              </>
            )}
          </ButtonContent>
          {link?.href && <ArrowIcon isExternal={hasExternalLink} />}
        </>
      )}
    </PrimaryCtaButton>
  )
}
